/**
 * Indirect standardization: the standardized mortality (or morbidity) ratio.
 *
 * Expected deaths E = Σ (reference rate in stratum i × person-years in stratum i), the
 * deaths the study population would have had if it had experienced the reference
 * population's stratum-specific rates. SMR = O / E, with O the deaths observed.
 *
 * - Interval: the exact (Garwood) Poisson interval for O, divided by E:
 *   lower = χ²(α/2; 2O) / 2E, upper = χ²(1 − α/2; 2O + 2) / 2E. It is what R's
 *   `poisson.test(O, T = E)` and `epitools::pois.exact` report.
 * - Test of SMR = 1: the exact two-sided Poisson p-value as `poisson.test` computes it
 *   (the total probability of outcomes no more likely than the one observed).
 */

import { poissonCdf, poissonPmf } from "@/lib/stats/distributions";
import { poissonCountInterval } from "@/lib/tool-kit/stats-research/proportions";

export interface SmrStratum {
  label: string;
  observed: number;
  personYears: number;
  /** The reference rate per `rateBase` person-years. */
  rate: number;
}

export interface SmrStratumResult extends SmrStratum {
  expected: number;
  /** O / E for this stratum; NaN when no deaths were expected. */
  ratio: number;
}

export interface SmrResult {
  observed: number;
  expected: number;
  smr: number;
  lower: number;
  upper: number;
  p: number;
  excess: number;
  level: number;
  strata: SmrStratumResult[];
}

/** Exact two-sided Poisson p-value for x events when m are expected (R's poisson.test). */
export function exactPoissonP(x: number, m: number): number {
  if (m === 0) {
    return x === 0 ? 1 : 0;
  }
  if (x === m) {
    return 1;
  }
  const relativeError = 1 + 1e-7;
  const d = poissonPmf(x, m);
  if (x < m) {
    let n = Math.ceil(2 * m - x);
    while (poissonPmf(n, m) > d) {
      n *= 2;
    }
    let y = 0;
    for (let i = Math.ceil(m); i <= n; i++) {
      if (poissonPmf(i, m) <= d * relativeError) {
        y += 1;
      }
    }
    return Math.min(1, poissonCdf(x, m) + (1 - poissonCdf(n - y, m)));
  }
  let y = 0;
  for (let i = 0; i <= Math.floor(m); i++) {
    if (poissonPmf(i, m) <= d * relativeError) {
      y += 1;
    }
  }
  return Math.min(1, poissonCdf(y - 1, m) + (1 - poissonCdf(x - 1, m)));
}

/** SMR from an observed count and an expected count. Null unless E > 0. */
export function smrFromCounts(
  observed: number,
  expected: number,
  level = 0.95
): Omit<SmrResult, "strata"> | null {
  if (!(expected > 0) || observed < 0 || !Number.isInteger(observed)) {
    return null;
  }
  const interval = poissonCountInterval(observed, level);
  return {
    observed,
    expected,
    smr: observed / expected,
    lower: interval.lower / expected,
    upper: interval.upper / expected,
    p: exactPoissonP(observed, expected),
    excess: observed - expected,
    level,
  };
}

/** SMR from strata of person-years and reference rates (per `rateBase`). */
export function standardizedMortalityRatio(
  strata: readonly SmrStratum[],
  rateBase: number,
  level = 0.95
): SmrResult | null {
  const rows = strata.map((stratum) => {
    const expected = (stratum.personYears * stratum.rate) / rateBase;
    return {
      ...stratum,
      expected,
      ratio: expected > 0 ? stratum.observed / expected : Number.NaN,
    };
  });
  const observed = rows.reduce((sum, row) => sum + row.observed, 0);
  const expected = rows.reduce((sum, row) => sum + row.expected, 0);
  const total = smrFromCounts(observed, expected, level);
  return total ? { ...total, strata: rows } : null;
}
